/**
 * D43a — Onboarding gate copy deck.
 *
 * Every member-facing string the extended onboarding gate (quizzes 1+2,
 * quiz-3 pop-up, post-completion nudge) introduces lives here. No surface
 * built in a later phase should hard-code a string this module already
 * covers — import from here instead.
 *
 * Voice: warm, knowledgeable-friend register (see restaurantUtils.tsx's
 * PersonalTaste™ toast copy and OnboardingTour.tsx for the existing DNA).
 * No emoji anywhere. Single trademark form `TasteMatch™` in any new string
 * (FR-A20) — pre-existing TasteMatch™/TasteMatches™ inconsistency elsewhere
 * is not this module's to fix.
 *
 * Coverage-vs-depth self-check (FR-A19) — which surface may claim that
 * quizzes 1+2 reach every taste dimension, and which may only claim depth:
 *
 * | Surface                        | May claim coverage? | Why |
 * |---------------------------------|----------------------|-----|
 * | welcome                          | No                   | Coverage isn't true yet — nothing is complete. |
 * | quiz1Transition                  | Yes                  | Truthfully: quizzes 1+2 together reach every dimension. |
 * | quiz3Popup                       | No                   | Gate is already satisfied; only depth (refine/deepen/sharpen) is honest. |
 * | nudge.full / nudge.deescalated    | No                   | Same reason as quiz3Popup — asked after 29/29 is already reached. |
 * | gateRelease                      | No                   | Celebrates completion without implying further quizzes add coverage. |
 * | menuGateBanner / tabBarGateToast | N/A                  | States the gate is unfinished, not what finishing it reaches. |
 * | guardUnavailable / offlineUnsubmitted | N/A            | Operational status, not a taste-profile claim. |
 *
 * Honest-effort figures used throughout (never the stale 3/4/3 estimates):
 * quiz 1 ≈ 5 min / 127 tiles, quiz 2 ≈ 4 min / 67 cards, quiz 3 ≈ 5 min / 139 cards.
 */

/** FR-A5, FR-A6, FR-A20 — onboarding welcome screen (OnboardingQuiz.tsx). */
export const welcomeCopy = {
  heading: "Let's Build Your PersonalTaste™",
  body: ['Start building your PersonalTaste™ profile by taking the first 2 quizzes'],
  ctaLabel: 'Start Quiz 1',
  timeEstimate: 'Total time estimate: 9 min',
  footnote: 'You can always update your preferences later',
};

/** FR-A7, FR-A10 — discrete two-segment step label. Never a percentage. */
export const stepIndicatorLabel = (step: 1 | 2): string => `Step ${step} of 2`;

/**
 * FR-A5, FR-A6, FR-A19 — quiz-1 completion transition panel.
 * Replaces the generic "Quiz Completed! 🎉" toast for the gated level-1
 * case. Communicates continuation, not completion, and (per FR-A19) may
 * truthfully claim coverage because quizzes 1+2 together reach every
 * dimension. No emoji.
 */
export const quiz1TransitionCopy = {
  heading: 'Nice work — on to quiz 2',
  body: "Together, quizzes 1 and 2 cover every taste dimension we track.",
};

/**
 * FR-A8 — remaining-effort figure. Always rounds up so it can never
 * understate the true remaining work; vague before wrong.
 */
export const remainingEffortLabel = (minutesLeft: number): string =>
  `~${Math.ceil(minutesLeft)} min left`;

/**
 * FR-A13–FR-A16, FR-A19 — quiz-3 pop-up.
 *
 * Depth only: approved verbs are refine/deepen/sharpen. Banned: unlocks,
 * completes, fills in, or any implication quiz 3 is required. No dimension
 * count, no fixed taste-taxonomy denominator, no fabricated statistic — the
 * "139 quick picks / ~5 minutes" figure is an honest effort disclosure
 * (mandatory, before the accept control), not a coverage claim.
 *
 * Dark-pattern self-check (spec §6):
 * (1) Can the member decline in one tap with no penalty? Yes — declineLabel
 *     is plain and unpenalized; declining returns the member to their
 *     original destination immediately with zero functional consequence.
 * (2) Does the copy avoid implying anything false or omitting the true
 *     coverage state? Yes — it states the profile is "already fully mapped"
 *     (true, since the gate is satisfied) and offers depth only, with no
 *     coverage or requirement claim for quiz 3.
 */
export const quiz3PopupCopy = {
  heading: 'Want to go deeper?',
  body: 'Your taste profile is already fully mapped. Quiz 3 sharpens it further with about 139 quick picks — roughly 5 minutes.',
  acceptLabel: 'Sharpen My Profile',
  declineLabel: 'Not now',
};

/**
 * FR-A17, FR-A18, FR-A19 — post-completion nudge, both emphasis variants.
 * Same constraints as the pop-up, including mandatory effort disclosure.
 * The de-escalated variant is quieter in wording, not just in styling.
 *
 * Dark-pattern self-check (spec §6): same two answers as quiz3PopupCopy —
 * both variants decline in one tap with no penalty, and neither variant
 * implies quiz 3 is required or that further quizzes add coverage.
 */
export const nudgeCopy = {
  full: {
    sentence: 'Want to sharpen your taste profile even more? Quiz 3 takes about 5 minutes.',
    acceptLabel: 'Sharpen It',
    declineLabel: 'Not now',
  },
  deescalated: {
    sentence: 'Still curious? Quiz 3 sharpens your profile in about 5 minutes.',
    acceptLabel: 'Take Quiz 3',
    declineLabel: 'No thanks',
  },
};

/**
 * FR-A4 — guard unavailable panel (D6). Rendered by Phase 4; authored here
 * so Phase 4 depends on this module rather than inventing the string.
 * Honest about what failed, does not blame the member, does not pretend
 * the check succeeded.
 */
export const guardUnavailableCopy = {
  body: "We couldn't check your setup. Check your connection and try again.",
  retryLabel: 'Retry',
  secondaryLabel: 'Go to Menu',
};

/**
 * FR-A10, FR-A11, NFR-10 — quiz completed while offline or unsubmitted.
 * Distinct from the generic failure copy in QuizTaking.tsx (kept in place
 * for the case with no draft to fall back on) — this string is used once
 * Phase 5's localStorage draft layer exists, and it must not read as lost
 * work. Warm, unalarming, specific about what happens next.
 */
export const offlineUnsubmittedCopy = {
  body: "You're offline, but your answers are safe on this device. We'll send them the moment you're back online.",
};

/**
 * FR-A1, FR-A2 — Menu page gate banner (second gate surface). Replaces
 * "Complete your initial quiz to unlock the full Paire experience.", which
 * is wrong under the extended two-quiz gate. Must agree with
 * tabBarGateToastCopy — a member can see both within seconds.
 */
export const menuGateBannerCopy = {
  description: 'Finish your taste quizzes to unlock the full Paire experience.',
  ctaLabel: 'Continue Quiz',
};

/**
 * FR-A1, FR-A2 — tab bar gate toast (third gate surface). Replaces
 * "Complete your initial quiz to unlock this part of the app.", same
 * defect as the Menu banner, same fix. Must agree with menuGateBannerCopy.
 */
export const tabBarGateToastCopy = {
  title: 'Quiz required',
  description: 'Finish your taste quizzes to unlock this part of the app.',
};

/**
 * FR-A9, FR-A19, FR-B1 — gate-release moment (quiz 2 completion). Nothing
 * implying further quizzes add coverage — 29 of 29 is already reached here.
 */
export const gateReleaseCopy = {
  heading: 'All Set',
  body: "Your PersonalTaste™ profile is ready — let's find your next favorite spot.",
};
